import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Anchor,
    Badge,
    Box,
    Button,
    Divider,
    Grid,
    Group,
    Modal,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
    deleteCase,
    getCase,
    getMonitoringState,
    listReports,
    listVisitsByCase,
    transitionCaseStatus,
    updateCase,
} from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { CopyableId } from '../../components/CopyableText';
import { EntityRef } from '../../components/EntityRef';
import { JsonBlock } from '../../components/JsonBlock';
import { DetailField, DetailFieldGrid } from '../../components/detail/DetailField';
import { CreateVisitModal } from '../../components/visits/CreateVisitModal';
import { MonitoringStateSection } from './MonitoringStateSection';
import { MonitoringLinksSection } from '../../components/monitoring-links/MonitoringLinksSection';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { isCaseEditable } from '../../lib/caseEditability';
import { reportLabel } from '../../lib/reportLabel';
import { CASE_STATUS_VALUES } from '../../types/domain';
import type { Report, Visit } from '../../types/api';

/** Card with the mockup's bold section title, optional badge, and optional action. */
function SectionCard({
    title,
    badge,
    action,
    children,
}: {
    title: string;
    badge?: React.ReactNode;
    action?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <DataPanel>
            <Group justify="space-between" align="center" mb="md" wrap="nowrap">
                <Group gap="xs" align="center">
                    <Text fw={700} size="md">
                        {title}
                    </Text>
                    {badge}
                </Group>
                {action}
            </Group>
            {children}
        </DataPanel>
    );
}

/** Timestamp a visit is ordered by — newest activity first. */
function visitActivityAt(visit: Visit): number {
    const stamp = visit.completedAt ?? visit.startedAt ?? visit.createdAt;
    return stamp ? new Date(stamp).getTime() : 0;
}

function statusColor(status?: string): string {
    const s = (status ?? '').toLowerCase();
    if (s.includes('approve') || s.includes('complete')) {
        return 'green';
    }
    if (s.includes('reject') || s.includes('fail') || s.includes('flag')) {
        return 'red';
    }
    return 'yellow';
}

export function CaseDetailPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { caseId = '' } = useParams<{ caseId: string }>();

    const [toStatus, setToStatus] = useState<string | null>(null);
    const [createVisitOpen, setCreateVisitOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [externalReferenceId, setExternalReferenceId] = useState('');
    const [priority, setPriority] = useState('');
    const [metadataJson, setMetadataJson] = useState('{}');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const lookups = useAdminLookups({ includeVisits: true });

    const caseQ = useQuery({
        queryKey: ['case', caseId],
        queryFn: () => getCase(caseId),
        enabled: Boolean(caseId),
    });

    const monitoringStateQ = useQuery({
        queryKey: ['monitoring-state', caseId],
        queryFn: () => getMonitoringState(caseId),
        enabled: Boolean(caseId),
    });

    const visitsQ = useQuery({
        queryKey: ['visits', 'for-case', caseId],
        queryFn: () => listVisitsByCase(caseId, { page: 1, limit: 500 }),
        enabled: Boolean(caseId),
    });

    const visitIds = (visitsQ.data?.data ?? []).map((v) => v.id);

    const reportsQ = useQuery({
        queryKey: ['reports', 'for-case', caseId, visitIds.join(',')],
        enabled: visitIds.length > 0,
        queryFn: async () => {
            const results = await Promise.all(
                visitIds.map((id) => listReports({ page: 1, limit: 50, visitId: id })),
            );
            return results.flatMap((result) => result.data);
        },
    });

    const transitionMutation = useMutation({
        mutationFn: () => transitionCaseStatus(caseId, { toStatus: toStatus! }),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.cases.statusUpdated') });
            setToStatus(null);
            void queryClient.invalidateQueries({ queryKey: ['case', caseId] });
            void queryClient.invalidateQueries({ queryKey: ['cases'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const updateMutation = useMutation({
        mutationFn: () => {
            let metadata: Record<string, unknown>;
            try {
                metadata = JSON.parse(metadataJson) as Record<string, unknown>;
            } catch {
                throw new Error(t('features.cases.invalidMetadataJson'));
            }
            return updateCase(caseId, {
                externalReferenceId: externalReferenceId.trim() || undefined,
                priority: priority.trim() || undefined,
                metadata,
            });
        },
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.cases.updated') });
            setEditOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['case', caseId] });
            void queryClient.invalidateQueries({ queryKey: ['cases'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: () => deleteCase(caseId),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.cases.deleted') });
            void queryClient.invalidateQueries({ queryKey: ['cases'] });
            navigate('/cases');
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const caseRow = caseQ.data;
    const visits = visitsQ.data?.data ?? [];
    const reports = reportsQ.data ?? [];

    if (!caseRow) {
        return <Text c="dimmed">{t('common.loading.case')}</Text>;
    }

    const empty = t('common.empty');
    const org = lookups.resolveOrganization(caseRow.organizationId);
    const isEditable = isCaseEditable(visits);
    const caseTypeLabel = caseRow.caseType
        ? t(`caseTypes.${caseRow.caseType}`, { defaultValue: caseRow.caseType })
        : null;

    const openEdit = () => {
        setExternalReferenceId(caseRow.externalReferenceId ?? '');
        setPriority(caseRow.priority ?? '');
        setMetadataJson(JSON.stringify(caseRow.metadata ?? {}, null, 2));
        setEditOpen(true);
    };

    /** Latest submission per visit, so a re-submitted visit shows its newest report. */
    const latestReportByVisit = new Map<string, Report>();
    for (const report of reports) {
        const current = latestReportByVisit.get(report.visitId);
        const currentAt = current?.submittedAt ? new Date(current.submittedAt).getTime() : -1;
        const nextAt = report.submittedAt ? new Date(report.submittedAt).getTime() : 0;
        if (!current || nextAt > currentAt) {
            latestReportByVisit.set(report.visitId, report);
        }
    }

    const orderedVisits = [...visits].sort((a, b) => visitActivityAt(b) - visitActivityAt(a));

    return (
        <Stack gap="lg">
            <Anchor
                component="button"
                type="button"
                c="dimmed"
                fz="sm"
                fw={600}
                onClick={() => navigate('/cases')}
                style={{ alignSelf: 'flex-start' }}
            >
                <Group gap={6}>
                    <IconArrowLeft size={15} />
                    {t('common.actions.back')}
                </Group>
            </Anchor>

            {/* Title left, actions right — mockup header row. */}
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                <Stack gap={2}>
                    <Title order={2}>{caseRow.externalReferenceId ?? caseTypeLabel ?? t('detail.case')}</Title>
                    <Text size="sm" c="dimmed">
                        {caseRow.id}
                    </Text>
                </Stack>
                <Group gap="sm">
                    {isEditable ? (
                        <Button variant="light" onClick={openEdit}>
                            {t('common.actions.edit')}
                        </Button>
                    ) : (
                        <Text size="xs" c="dimmed" maw={220}>
                            {t('features.cases.notEditableAfterReview')}
                        </Text>
                    )}
                    {visits.length === 0 ? (
                        <Button
                            variant="light"
                            color="red"
                            loading={deleteMutation.isPending}
                            onClick={() => setDeleteOpen(true)}
                        >
                            {t('common.actions.delete')}
                        </Button>
                    ) : null}
                </Group>
            </Group>

            {/* Case status control sits directly under the header, as in the mockup. */}
            <Group align="flex-end" gap="sm">
                <Select
                    label={t('fields.status')}
                    placeholder={caseRow.status ?? undefined}
                    w={260}
                    data={CASE_STATUS_VALUES.map((status) => ({
                        value: status,
                        label: t(`caseStatuses.${status}`, { defaultValue: status }),
                    }))}
                    value={toStatus}
                    onChange={setToStatus}
                />
                <Button
                    disabled={!toStatus || toStatus === caseRow.status}
                    loading={transitionMutation.isPending}
                    onClick={() => transitionMutation.mutate()}
                >
                    {t('common.actions.transition')}
                </Button>
            </Group>

            {/* Observation state & changes — shown for every case type once a
                visit has produced an observation (backend returns an empty
                timeline otherwise). */}
            {monitoringStateQ.data && monitoringStateQ.data.timeline.length > 0 && (
                <MonitoringStateSection state={monitoringStateQ.data} />
            )}

            <Grid gap="lg" align="flex-start">
                {/* Left column — 1.4fr in the mockup. */}
                <Grid.Col span={{ base: 12, lg: 7 }}>
                    <Stack gap="lg">
                        <SectionCard
                            title={t('detail.case')}
                            badge={
                                caseTypeLabel ? (
                                    <Badge variant="light">{caseTypeLabel}</Badge>
                                ) : undefined
                            }
                        >
                            <DetailFieldGrid>
                                <DetailField
                                    label={t('fields.organization')}
                                    value={
                                        <EntityRef
                                            title={org.title}
                                            id={org.id}
                                            href={`/organizations?id=${caseRow.organizationId}`}
                                        />
                                    }
                                />
                                <DetailField
                                    label={t('fields.status')}
                                    value={
                                        caseRow.status ? (
                                            <Badge variant="light" color={statusColor(caseRow.status)}>
                                                {t(`caseStatuses.${caseRow.status}`, {
                                                    defaultValue: caseRow.status,
                                                })}
                                            </Badge>
                                        ) : (
                                            empty
                                        )
                                    }
                                />
                                <DetailField
                                    label={t('fields.externalReference')}
                                    value={caseRow.externalReferenceId ?? empty}
                                />
                                <DetailField
                                    label={t('fields.priority')}
                                    value={caseRow.priority ?? empty}
                                />
                                <DetailField
                                    label={t('fields.caseId')}
                                    value={<CopyableId id={caseRow.id} />}
                                />
                                <DetailField
                                    label={t('fields.caseSchemaId')}
                                    value={caseRow.caseSchemaId ?? empty}
                                />
                                <DetailField
                                    label={t('fields.requestedAt')}
                                    value={
                                        caseRow.requestedAt
                                            ? new Date(caseRow.requestedAt).toLocaleString()
                                            : empty
                                    }
                                />
                                <DetailField
                                    label={t('fields.dueAt')}
                                    value={
                                        caseRow.dueAt
                                            ? new Date(caseRow.dueAt).toLocaleString()
                                            : empty
                                    }
                                />
                                <DetailField
                                    label={t('fields.lastObservedAt')}
                                    value={
                                        caseRow.lastObservedAt
                                            ? new Date(caseRow.lastObservedAt).toLocaleString()
                                            : empty
                                    }
                                />
                                <DetailField
                                    label={t('fields.created')}
                                    value={
                                        caseRow.createdAt
                                            ? new Date(caseRow.createdAt).toLocaleString()
                                            : empty
                                    }
                                />
                            </DetailFieldGrid>
                        </SectionCard>

                        <SectionCard title={t('detail.poisOnCase')}>
                            {caseRow.casePois && caseRow.casePois.length > 0 ? (
                                <Stack gap="sm">
                                    {caseRow.casePois.map((link) => (
                                        <Paper key={link.id} p="sm" radius="md" bg="var(--mantine-color-default-hover)">
                                            <Group justify="space-between" wrap="nowrap">
                                                <EntityRef
                                                    title={
                                                        link.poi?.name ??
                                                        link.poi?.claimedAddress ??
                                                        link.poiId ??
                                                        t('entities.poi')
                                                    }
                                                    id={link.poiId}
                                                    href={`/pois?id=${link.poiId}`}
                                                />
                                                {link.role ? (
                                                    <Badge variant="light" color="gray">
                                                        {link.role}
                                                    </Badge>
                                                ) : null}
                                            </Group>
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : (
                                <Text size="sm" c="dimmed">
                                    {t('detail.noPois')}
                                </Text>
                            )}
                        </SectionCard>

                        <MonitoringLinksSection caseRecord={caseRow} />

                        <SectionCard
                            title={t('detail.visitHistorySection', { count: visits.length })}
                            action={
                                <Button size="xs" variant="light" onClick={() => setCreateVisitOpen(true)}>
                                    {t('features.visits.createAction')}
                                </Button>
                            }
                        >
                            {orderedVisits.length === 0 ? (
                                <Text size="sm" c="dimmed">
                                    {t('detail.noVisitHistory')}
                                </Text>
                            ) : (
                                <Stack gap={0}>
                                    {orderedVisits.map((v, index) => {
                                        const officer = lookups.resolveUser(v.fieldOfficerId);
                                        const visitLabel = lookups.resolveVisit(v.id, v);
                                        const report = latestReportByVisit.get(v.id);
                                        const activityAt = visitActivityAt(v);
                                        return (
                                            <Box key={v.id}>
                                                {index > 0 ? <Divider /> : null}
                                                <Group
                                                    align="flex-start"
                                                    wrap="nowrap"
                                                    gap="sm"
                                                    py="sm"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/visits/${v.id}`)}
                                                >
                                                    <Box
                                                        w={9}
                                                        h={9}
                                                        mt={7}
                                                        style={{
                                                            borderRadius: '50%',
                                                            flexShrink: 0,
                                                            backgroundColor: `var(--mantine-color-${statusColor(v.status)}-filled)`,
                                                        }}
                                                    />
                                                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                                        <Group gap="xs" wrap="wrap">
                                                            <Text size="sm" fw={700}>
                                                                {v.visitType
                                                                    ? t(`visitTypes.${v.visitType}`, {
                                                                          defaultValue: v.visitType,
                                                                      })
                                                                    : visitLabel.title}
                                                            </Text>
                                                            {v.status ? (
                                                                <Badge
                                                                    size="sm"
                                                                    variant="light"
                                                                    color={statusColor(v.status)}
                                                                >
                                                                    {v.status}
                                                                </Badge>
                                                            ) : null}
                                                        </Group>
                                                        <Text size="xs" c="dimmed">
                                                            {[
                                                                activityAt
                                                                    ? new Date(activityAt).toLocaleString()
                                                                    : null,
                                                                v.fieldOfficerId
                                                                    ? `${t('fields.fieldOfficer')}: ${officer.title}`
                                                                    : null,
                                                                report ? reportLabel(report) : null,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' · ') || empty}
                                                        </Text>
                                                    </Stack>
                                                </Group>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </SectionCard>
                    </Stack>
                </Grid.Col>

                {/* Right column — 1fr in the mockup. */}
                <Grid.Col span={{ base: 12, lg: 5 }}>
                    <Stack gap="lg">
                        <SectionCard title={t('detail.subjectsOnCase')}>
                            {caseRow.caseSubjects && caseRow.caseSubjects.length > 0 ? (
                                <Stack gap="md">
                                    {caseRow.caseSubjects.map((link) => (
                                        <Group key={link.id} justify="space-between" wrap="nowrap">
                                            <EntityRef
                                                title={
                                                    link.subject?.fullName ??
                                                    link.subjectId ??
                                                    t('entities.subject')
                                                }
                                                id={link.subjectId}
                                                href={`/subjects?id=${link.subjectId}`}
                                            />
                                            {link.role ? (
                                                <Badge variant="light" color="gray">
                                                    {link.role}
                                                </Badge>
                                            ) : null}
                                        </Group>
                                    ))}
                                </Stack>
                            ) : (
                                <Text size="sm" c="dimmed">
                                    {t('detail.noSubjects')}
                                </Text>
                            )}
                        </SectionCard>

                        <SectionCard title={t('detail.reportsOnCase')}>
                            {reports.length === 0 ? (
                                <Text size="sm" c="dimmed">
                                    {t('detail.noReportsOnCase')}
                                </Text>
                            ) : (
                                <Stack gap="sm">
                                    {reports.map((r) => (
                                        <Group key={r.id} justify="space-between" wrap="nowrap">
                                            <EntityRef
                                                title={reportLabel(r)}
                                                id={r.id}
                                                href={`/reports?id=${r.id}`}
                                            />
                                            <Text size="xs" c="dimmed">
                                                {r.submittedAt
                                                    ? new Date(r.submittedAt).toLocaleDateString()
                                                    : t('common.draft')}
                                            </Text>
                                        </Group>
                                    ))}
                                </Stack>
                            )}
                        </SectionCard>

                        {caseRow.metadata && Object.keys(caseRow.metadata).length > 0 ? (
                            <SectionCard title={t('detail.metadata')}>
                                <JsonBlock value={caseRow.metadata} />
                            </SectionCard>
                        ) : null}
                    </Stack>
                </Grid.Col>
            </Grid>

            <CreateVisitModal
                opened={createVisitOpen}
                onClose={() => setCreateVisitOpen(false)}
                caseId={caseId}
                onCreated={() => {
                    void queryClient.invalidateQueries({ queryKey: ['visits', 'for-case', caseId] });
                }}
            />

            <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={t('features.cases.editTitle')}>
                <Stack gap="md">
                    <TextInput
                        label={t('fields.externalReference')}
                        value={externalReferenceId}
                        onChange={(e) => setExternalReferenceId(e.currentTarget.value)}
                    />
                    <TextInput
                        label={t('fields.priority')}
                        value={priority}
                        onChange={(e) => setPriority(e.currentTarget.value)}
                    />
                    <Textarea
                        label={t('detail.metadata')}
                        minRows={6}
                        value={metadataJson}
                        onChange={(e) => setMetadataJson(e.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setEditOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>
                            {t('common.actions.save')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Modal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title={t('features.cases.deleteTitle')}>
                <Stack gap="md">
                    <Text size="sm">{t('features.cases.deleteConfirm')}</Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setDeleteOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button color="red" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                            {t('common.actions.delete')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}
